import { defineStore } from 'pinia'
import { ref } from 'vue'

import { useFetchStore } from '@/stores/fetch'
import type { IDriver } from '@/types/Driver'
import { DriverFilter } from '@/types/Driver'

/*
 * The register, held once for the selects that offer it.
 *
 * Three screens ask the same question — the vehicle's "assign a driver", the logbook's
 * entry dialog and its bulk dialog — and the answer is a few hundred rows that change
 * when somebody edits the register and at no other time. So it is loaded ON DEMAND, the
 * first time a select needs it, and thrown away by every write: the drivers page calls
 * invalidate() after an add, an edit or a delete, and the next select that opens asks
 * again.
 *
 * It is NOT polled. Nothing about this list is news — unlike the alert count beside it,
 * a driver who was hired while somebody had the trips page open is not something the
 * page has to notice by itself.
 */
export const useDriversStore = defineStore('drivers', () => {
    const fetchStore = useFetchStore()

    /** Everybody, active and not: an old drive's driver may have left. */
    const drivers = ref<IDriver[]>([])
    const loading = ref(false)
    const loaded  = ref(false)

    const load = async (force = false) => {
        if (loading.value || (loaded.value && !force)) return

        loading.value = true

        const { statusCode, data } = await fetchStore
            .fetch(`/drivers/?active=${DriverFilter.All}`)
            .get()
            .json<IDriver[]>()

        if (statusCode.value === 200) {
            drivers.value = data.value ?? []
            loaded.value  = true
        }

        loading.value = false
    }

    /** The people a new assignment or entry may name. */
    const active = () => drivers.value.filter((driver) => driver.active)

    const byId = (id: string): IDriver | undefined => drivers.value.find((driver) => driver.id === id)

    const invalidate = () => { loaded.value = false }

    const reset = () => {
        drivers.value = []
        loaded.value  = false
    }

    return {
        drivers, loading, loaded,
        load, active, byId, invalidate, reset,
    }
})
